import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Eye, BadgeCheck, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { API_CP, dateFR, erreur } from './constantes';
import { PointCompteRendu } from './PointCompteRendu';
import { SyntheseBloc } from './SyntheseBloc';
import { InfosPV } from './InfosPV';

const INFOS = ['resume_brut', 'synthese', 'secretaire_nom', 'documents_examines', 'prochain_conseil'];
const extraire = (c) => ({
  points: c.ordre_du_jour.map((p) => ({ ...p, presente: p.presente || '', propositions: p.propositions || '', avis: p.avis || '',
    decisions: p.decisions.map((d) => ({ ...d, preuve: d.preuve || '' })) })),
  ...Object.fromEntries(INFOS.map((k) => [k, c[k] || ''])),
});
const empreinte = (b) => JSON.stringify([b.points.map((p) => [p.presente, p.propositions, p.avis,
  p.decisions.map((d) => [d.texte, d.responsable || '', d.echeance || '', d.preuve || ''])]), ...INFOS.map((k) => b[k])]);

export const OngletCompteRendu = ({ cp, onPdf }) => {
  const { conseil, enregistrer, recharger, setConseil } = cp;
  const [b, setB] = useState(() => extraire(conseil));
  const [occupe, setOccupe] = useState('');
  const [aides, setAides] = useState({});
  const api = API_CP(conseil.annee);
  const modifie = empreinte(b) !== empreinte(extraire(conseil));
  useEffect(() => { axios.get('/conseil-perfectionnement/aides').then(({ data }) => setAides(data)).catch((e) => erreur(e, 'Aide en séance indisponible')); }, []);

  const sauver = async (msg = 'Compte rendu enregistré') => {
    if (b.points.some((p) => p.decisions.some((d) => !d.texte.trim()))) { toast.error('Chaque décision doit avoir un intitulé'); return null; }
    const { points, ...infos } = b;
    const data = await enregistrer({ ordre_du_jour: points, ...infos }, msg);
    if (data) setB(extraire(data));
    return data;
  };
  const avant = async () => (modifie ? sauver(null) : conseil);
  const tache = (nom, fn) => async (...args) => { setOccupe(nom); try { await fn(...args); } finally { setOccupe(''); } };

  const ia = tache('ia', async () => {
    if (!(await avant())) return;
    if (b.synthese.trim() && !window.confirm('Remplacer la synthèse actuelle par une nouvelle version rédigée par l\'IA ?')) return;
    try {
      const { data } = await axios.post(`${api}/synthese-ia`);
      setB((x) => ({ ...x, synthese: data.synthese }));
      toast.success('Synthèse rédigée : relisez-la puis enregistrez');
    } catch (e) { erreur(e, 'Assistant IA indisponible'); }
  });
  const versPlan = (i) => async (k) => {
    setOccupe(`plan-${i}-${k}`);
    try {
      const d = (await avant())?.ordre_du_jour[i]?.decisions[k];
      if (!d?.decision_id) return;
      await axios.post(`${api}/decisions/${d.decision_id}/plan`);
      toast.success("Décision ajoutée au plan d'amélioration Qualiopi");
      const data = await recharger();
      if (data) setB(extraire(data));
    } catch (e) { erreur(e, 'Ajout au plan impossible'); } finally { setOccupe(''); }
  };
  const apercu = tache('apercu', async () => { if (await avant()) onPdf(null, 'Procès-verbal', `${api}/compte-rendu.pdf`); });
  const valider = tache('valider', async () => {
    if (!(await avant())) return;
    try {
      const { data } = await axios.post(`${api}/valider`);
      setConseil(data);
      toast.success('Compte rendu validé et rangé dans le dossier Qualiopi');
    } catch (e) { erreur(e, 'Validation impossible'); }
  });

  return (
    <div className="space-y-5" data-testid="cp-compte-rendu">
      {conseil.valide_le && (
        <p className="flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800" data-testid="cp-cr-valide">
          <CheckCircle2 className="h-4 w-4" /> Compte rendu validé le {dateFR(conseil.valide_le)} : il figure dans le dossier Qualiopi (indicateur 30). Toute modification demandera une nouvelle validation.
        </p>
      )}
      <InfosPV b={b} setB={setB} />
      {b.points.length === 0 && <p className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-400" data-testid="cp-cr-vide">Ajoutez d'abord des points à l'ordre du jour.</p>}
      {b.points.map((p, i) => (
        <PointCompteRendu key={p.point_id || i} p={p} i={i} aide={aides[p.aide_cle]} occupe={occupe} onPlan={versPlan(i)}
          onChange={(np) => setB((x) => ({ ...x, points: x.points.map((q, j) => (j === i ? np : q)) }))} />
      ))}
      <SyntheseBloc b={b} setB={setB} onIA={ia} occupe={occupe} />
      <div className="sticky bottom-3 z-10 flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white/90 p-3 shadow-lg backdrop-blur-md">
        <Button onClick={() => sauver()} disabled={!modifie || !!occupe} className="rounded-full bg-[#0F172A] px-5" data-testid="cp-cr-enregistrer"><Save className="mr-2 h-4 w-4" /> Enregistrer</Button>
        <Button variant="outline" onClick={apercu} disabled={!!occupe} className="rounded-full" data-testid="cp-cr-apercu"><Eye className="mr-2 h-4 w-4" /> Aperçu du procès-verbal</Button>
        <Button onClick={valider} disabled={!!occupe} className="rounded-full bg-[#8DC63F] px-5 text-[#0F172A] hover:bg-[#7DB52F]" data-testid="cp-cr-valider">
          {occupe === 'valider' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <BadgeCheck className="mr-2 h-4 w-4" />} Valider le compte rendu
        </Button>
        {modifie && <span className="text-xs font-medium text-amber-600" data-testid="cp-cr-modifie">Modifications non enregistrées</span>}
      </div>
    </div>
  );
};
